import {
  arrow,
  autoUpdate,
  computePosition,
  flip,
  offset,
  Placement,
  shift,
} from '@floating-ui/dom';
import { isKeydown, TmElement } from '@thiememeulenhoff/lit-sdk';
import { html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { styles, tag } from './tooltip.styles';

@customElement(tag)
export class TooltipComponent extends TmElement {
  readonly #primaryMouseButton = 1; // By default, the left mouse button

  static override styles = styles;

  @property({ reflect: true })
  variant: 'primary' | 'secondary' | 'error' = 'primary';

  @property({ reflect: true })
  placement: Placement = 'right';

  @property()
  offset = '8';

  @property({ type: Boolean })
  disabled = false;

  @query('#tooltip')
  tooltip!: HTMLElement;

  @query('#arrow')
  arrow!: HTMLElement;

  trigger!: HTMLElement;

  @state()
  hide = false;

  timeoutId?: number;

  #blocked = false;

  #destroyObservers?: () => void;

  protected override onCreate = async () => {
    await this.updateComplete;

    this.#setup();
  };

  readonly #blockTooltipTemporarily = () => {
    this.#blocked = true;
  };

  protected override onDestroy = () => {
    this.#destroyObservers?.();

    document.removeEventListener(
      'closed-dialog',
      this.#blockTooltipTemporarily,
    );
  };

  protected get triggerElement() {
    return this.trigger;
  }

  override render() {
    return html`
      <slot></slot>

      <div
        popover
        part="tooltip"
        id="tooltip"
        class="tooltip"
        data-test="tooltip"
        hide=${this.hide || nothing}
      >
        <slot name="content"></slot>
        <div id="arrow" class="arrow"></div>
      </div>
    `;
  }

  // Prevent error on older browsers
  #showPopover() {
    if (typeof this.tooltip.showPopover === 'function') {
      this.tooltip.showPopover();
    }
  }

  // Prevent error on older browsers
  #hidePopover() {
    if (typeof this.tooltip.hidePopover === 'function') {
      this.tooltip.hidePopover();
    }
  }

  forceOpen() {
    this.#clearTimeout();

    this.#attachTooltip();
    this.#showPopover();
  }

  #clearTimeout() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
    }
  }

  #open = (event: Event) => {
    if (this.disabled) {
      return;
    }

    // bitwise operator is required for the buttons property
    if (
      event instanceof MouseEvent &&
      event.buttons & this.#primaryMouseButton
    ) {
      return; // don't show tooltip when mouse is down
    }

    this.#clearTimeout();

    this.#attachTooltip();
    this.#showPopover();
  };

  #close = () => {
    this.hide = true;

    const transitionMs =
      parseFloat(window.getComputedStyle(this.tooltip).transitionDuration) *
      1000;

    this.timeoutId = window.setTimeout(() => {
      this.hide = false;
      this.#hidePopover();
    }, transitionMs);
  };

  #isPopoverOpen() {
    return this.tooltip.matches(':popover-open');
  }

  #toggle = (event: Event) => {
    if (this.#isPopoverOpen()) this.#close();
    else this.#open(event);
  };

  #handleFocusOut = (event: Event) => {
    if (this && !event.composedPath().includes(this)) this.#close();
  };

  /**
   * TODO this is done to prevent permanent tooltips if a dialog has the same trigger as the tooltip
   *      When you close the dialog, the tooltip opens and won't close till you click
   *      This is a hack, a better way would be to use the 'sourceCapabilities' var inside the event
   *      This shows the input device, which isn't there when it's opened when closing a dialog.
   *      This wasn't used because Safari and Firefox does not support it (yet?).
   */
  #handleFocusIn = (event: Event) => {
    if (!this.#blocked) this.#open(event);
    this.#blocked = false;
  };

  #handleKeydown = (event: Event) => {
    if (isKeydown(event, 'Escape')) this.#close();

    // TODO this needs to be added because of the #handleFocusOut implementation.
    //      Tabs are not handled correctly anymore
    if (isKeydown(event, 'Tab')) this.#close();
  };

  #setup = () => {
    this.#setTrigger();
    this.#setListeners();
    this.#attachTooltip();

    this.#destroyObservers = autoUpdate(
      this.trigger,
      this.tooltip,
      this.#attachTooltip,
    );
  };

  #setListeners() {
    // matchMedia('(pointer:fine)'): Checks if the primary input device has fine precision (e.g., a mouse).
    // matchMedia('(hover:hover)'): Checks if the primary input device supports hover (e.g., a mouse pointer).
    const hasMouseInput: boolean =
      matchMedia('(pointer:fine)').matches &&
      matchMedia('(hover:hover)').matches;

    if (hasMouseInput) {
      this.listen('focusin', this.#handleFocusIn, this.triggerElement);
      this.listen('focusout', this.#handleFocusOut, this.triggerElement);
      this.listen('mouseenter', this.#open, this.triggerElement);
      this.listen('mouseleave', this.#close, this);
    }

    this.listen('keydown', this.#handleKeydown);
    this.listen('click', this.#toggle, this.trigger);

    document.addEventListener('closed-dialog', this.#blockTooltipTemporarily);
  }

  #setTrigger = () => {
    const trigger = this.slottedChildren?.[0] as HTMLElement;
    if (!trigger) throw Error('No trigger element found');
    this.trigger = trigger;
  };

  #attachTooltip = () => {
    computePosition(this.trigger, this.tooltip, {
      placement: this.placement,
      middleware: [
        shift({ padding: +this.offset }),
        offset(+this.offset),
        arrow({ element: this.arrow }),
        flip(),
      ],
    }).then(({ x, y, placement, middlewareData }) => {
      Object.assign(this.tooltip.style, {
        'inset-inline-start': `${x}px`,
        'inset-block-start': `${y}px`,
      });
      this.tooltip.style.setProperty('--offset', `${this.offset}px`);

      const arrowX = middlewareData.arrow?.x;
      const arrowY = middlewareData.arrow?.y;
      const staticSide = {
        top: 'bottom',
        right: 'left',
        bottom: 'top',
        left: 'right',
      }[placement.split('-')[0]];

      if (staticSide !== undefined) {
        Object.assign(this.arrow.style, {
          left: arrowX != null ? `${arrowX}px` : '',
          top: arrowY != null ? `${arrowY}px` : '',
          right: '',
          bottom: '',
          [staticSide]: '-4px',
        });
      }
    });
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'a-tooltip': TooltipComponent;
  }
}
